import { CarFeatureGroup } from '@oneaudi/onegraph-client';
import { Headline, HeadlineProps, Layout, Skeleton, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { Fragment, useCallback, useMemo } from 'react';
import { ConfigurationSection } from '~/app/compositions/content/components/configuration-section';
import { ConfiguredFeature } from '~/app/compositions/content/components/configured-feature';
import { TopSectionStyle } from '~/app/compositions/content/components/styles/section-style';
import { ConfigurationOverviewPriceWrapperStyle } from '~/app/compositions/content/configuration-overview-style';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { MoreInformation } from '~/app/pages/configurator/components/more-information-dialog';
import { useCatalog } from '~/graphql/hooks/use-configurator';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';

import { OmnibusDialog } from '~/app/compositions/content/components/omnibus-dialog/omnibus-dialog';
import { useMarketConfig } from '~/app/market-config';
import { useSelectedEquipment } from '~/app/pages/summary/hooks/use-selected-equipment';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';
import { ContentOrPackage, FeatureOrPackage } from '~/graphql/types';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { FinancingData } from '~/app/contexts/reducers/app-reducer';

interface TopSectionProps {
	techDataPresent: boolean;
}

export const TopSection = ({ techDataPresent }: TopSectionProps) => {
	const { i18n } = useI18n();
	const { openDialog } = useDialogs();
	const setCurrentView = useSetAtom(state.currentView);
	const { config } = useMarketConfig();
	const headlineVariant: HeadlineProps['variant'] = { type: 'headline', order: '2', weight: 'normal' };

	const trackClickEvent = useCallback((eventName: string, label: string, targetURL?: string) => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (configuration overview) - click to ${eventName}`,
			componentName: 'configurator-configuration-overview',
			label,
			targetURL,
		});
	}, []);

	return (
		<>
			{}
			<TopSectionStyle tabIndex={0} aria-labelledby={'overview-heading'}>
				<Headline id={'overview-heading'} tag="h2" variant={headlineVariant}>
					{i18n('ngc.summary.overview')}
				</Headline>
				<Layout
					columnGap="16px"
					rowGap="16px"
					wrap={{
						default: 'wrap',
					}}>
					<TextLink
						size="md"
						variant="standalone"
						href="#configurator"
						onClick={() => {
							setCurrentView('configurator');
							trackClickEvent(
								'change configuration',
								i18n('ngc.actions.changeConfiguration'),
								`${window.location.href.replace('summary', 'configurator')}`,
							);
						}}
						data-testid={DATA_TEST_ID.SUMMARY_CHANGE_CONFIGURATION_BUTTON}>
						{i18n('ngc.actions.changeConfiguration')}
					</TextLink>
					{config.hasTechnicalDataEnabled && techDataPresent && (
						<TextLink
							size="md"
							variant="standalone"
							onClick={() => {
								openDialog('technicalData');
								trackClickEvent('open technical data layer', i18n('ngc.actions.viewTechnicalData'));
							}}
							data-testid={DATA_TEST_ID.SUMMARY_TECH_DATA_BUTTON}>
							{i18n('ngc.actions.viewTechnicalData')}
						</TextLink>
					)}

					<TextLink
						size="md"
						variant="standalone"
						onClick={() => {
							openDialog('standardEquipment');
							trackClickEvent('open standard equipment layer', i18n('ngc.actions.showStandardEquipment'));
						}}
						data-testid={DATA_TEST_ID.SUMMARY_STANDARD_EQUIPMENT_BUTTON}>
						{i18n('ngc.actions.showStandardEquipment')}
					</TextLink>
				</Layout>
			</TopSectionStyle>
		</>
	);
};

export const FeaturesAndPackages = () => {
	const { i18n } = useI18n();
	const vatConfig = useVATConfig();
	const disclaimerText = useAtomValue(state.financingDisclaimerText);
	const {
		configuration: { featuresAndPackages },
	} = useSelectedEquipment();

	const replaceExteriorColorWithTranslationKey = (group: CarFeatureGroup) => {
		switch (group.id) {
			case 'Exterior_Colors':
				return i18n('ngc.types.ConfiguredCarFeature.EXTERIOR_COLOR');
			case 'Exclusive_Exterior_Colors':
				return i18n('ngc.types.ConfiguredCarFeature.EXCLUSIVE_EXTERIOR_COLOR');
			case 'Body_Colors':
				return i18n('ngc.types.ConfiguredCarFeature.BODY_COLOR');
			case 'Roof_Colors':
				return i18n('ngc.types.ConfiguredCarFeature.ROOF_COLOR');
			default:
				return group.name;
		}
	};

	const items = useMemo(() => {
		let _items = structuredClone(featuresAndPackages);

		const hasRoofAndBodyColors = ['Body_Colors', 'Roof_Colors'].every((groupId) =>
			_items.some((feature) => feature.id === groupId),
		);

		// filter out exterior color when exclusive color is selected or when roof and body colors are present (e.g: a1 or a7)
		const hasExclusiveColor = _items.some((feature) => feature.id === 'Exclusive_Exterior_Colors');
		if (hasExclusiveColor || hasRoofAndBodyColors) {
			_items = _items.filter((feature) => feature.id !== 'Exterior_Colors');
		}

		// filter out seat cover and material in case of interior package is selected to prevent doubled entries
		const hasInteriorPackage = _items.some((feature) => feature.id.toLowerCase().includes('interior_lines'));
		if (hasInteriorPackage) {
			return _items.filter(
				(feature) =>
					!feature.id.toLowerCase().includes(':interior_tiles') &&
					!feature.id.toLowerCase().includes('material'),
			);
		}

		// filter out material in case of interior is selected to prevent doubled entries
		const hasInteriorTiles = _items.some((feature) => feature.id.toLowerCase().includes(':interior_tiles'));
		if (hasInteriorTiles) {
			return _items.filter((feature) => !feature.id.toLowerCase().includes('material'));
		}

		return _items;
	}, [JSON.stringify(featuresAndPackages)]);

	const onMoreInformationClick = useCallback(
		(open: boolean) => {
			appTrackingService.summaryClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `configurator (configuration overview) - click to ${open ? 'open' : 'close'} details layer`,
				componentName: open ? 'configurator-configuration-overview' : 'details-layer',
				label: open ? i18n('ngc.actions.moreInfo') : '',
			});
			appTrackingService.updateLayerComponent(LayerType.DetailsLayer, open ? 'open' : 'close');
		},
		[i18n],
	);

	return (
		<>
			{items.map((group) => (
				<Fragment key={group.id}>
					<ConfigurationSection title={replaceExteriorColorWithTranslationKey(group as CarFeatureGroup)}>
						{group.families.map((family) => (
							<Fragment key={family.id}>
								{family.items.map((item) => {
									return (
										<>
											{}
											<div key={item.pr7} tabIndex={0}>
												<ConfiguredFeature
													key={item.pr7}
													vatConfig={vatConfig.feature}
													item={item as FeatureOrPackage}
													disclaimer={disclaimerText}
													moreInformation={
														<MoreInformation
															onOpen={onMoreInformationClick}
															featureOrPackage={item as ContentOrPackage}
														/>
													}
												/>
											</div>
										</>
									);
								})}
							</Fragment>
						))}
					</ConfigurationSection>
				</Fragment>
			))}
		</>
	);
};

export const Accessories = () => {
	const {
		configuration: { accessories },
	} = useSelectedEquipment();
	const { i18n } = useI18n();
	const vatConfig = useVATConfig();
	const disclaimerText = useAtomValue(state.financingDisclaimerText);

	const onMoreInformationClick = useCallback(
		(open: boolean) => {
			appTrackingService.summaryClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `configurator (configuration overview) - click to ${open ? 'open' : 'close'} details layer`,
				componentName: 'configurator-configuration-overview',
				label: i18n('ngc.actions.moreInfo'),
			});
		},
		[i18n],
	);

	return (
		<>
			{accessories && accessories.length > 0 && (
				<>
					<ConfigurationSection title={i18n('ngc.equipment.audiOriginalAccessories')}>
						{accessories.map((item) => (
							<ConfiguredFeature
								vatConfig={vatConfig.feature}
								key={item.pr7}
								item={item}
								disclaimer={disclaimerText}
								moreInformation={
									<MoreInformation onOpen={onMoreInformationClick} featureOrPackage={item} />
								}
							/>
						))}
					</ConfigurationSection>
				</>
			)}
		</>
	);
};

export const SummarySection = () => {
	const { priceSummary } = useCatalog();
	const basePrice = priceSummary?.parts?.find((part) => part.type === 'model');
	const additionalCosts = priceSummary?.parts?.find((part) => part.type === 'options');
	const vatConfig = useVATConfig();
	const { i18n } = useI18n();
	const { loading: isLoadingSelectedEquipment } = useSelectedEquipment();
	const { formatCurrency } = useCurrencyFormatter();
	const { config } = useMarketConfig();

	const priceParts = getFilteredPriceParts(priceSummary, config)?.filter(
		(p) => p.type !== 'model' && p.type !== 'options',
	);

	return (
		<>
			{isLoadingSelectedEquipment ? (
				<>
					{Array.from({ length: 3 }).map((_, index) => (
						<Skeleton key={index} variant="oval" height={'324px'} />
					))}
				</>
			) : (
				<div className="summary-section">
					{config.showPrices && (
						<>
							<ConfigurationOverviewPriceWrapperStyle>
								{}
								<div className="summary__price-headline" tabIndex={0}>
									<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
										{i18n('ngc.priceDetails.model')}
									</Text>
								</div>
								{}
								<div className="summary__price-value" tabIndex={0}>
									<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
										{formatCurrency(basePrice?.price, vatConfig.trimlineAndEngine)}
									</Text>
								</div>
								{config.omnibus.enabled && (
									<div className="summary__omnibus-container" tabIndex={0}>
										<OmnibusDialog />
									</div>
								)}
							</ConfigurationOverviewPriceWrapperStyle>

							<ConfigurationOverviewPriceWrapperStyle tabIndex={0}>
								<div className="summary__price-headline">
									<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
										{i18n('ngc.currency.additionalCost')}
									</Text>
								</div>
								<div className="summary__price-value">
									<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
										{formatCurrency(additionalCosts?.price, vatConfig.priceParts['options'])}
									</Text>
								</div>
							</ConfigurationOverviewPriceWrapperStyle>
						</>
					)}
					<FeaturesAndPackages />
					{!config.uiPreferences.prices.nonFinanceablePriceParts?.includes('accessories') && <Accessories />}
					{config.showPrices && config.showFeesOnSummaryPage && (
						<div className="summary__price-list--spacious">
							{priceParts?.map((part) => {
								const labelId = `summary-price-label-${part}`;
								const priceId = `summary-price-${part}`;
								return (
									<ConfigurationOverviewPriceWrapperStyle
										key={part.type}
										tabIndex={0}
										aria-labelledby={`${labelId} ${priceId}`}>
										<div className="summary__price-headline">
											<Text
												id={labelId}
												tag={'p'}
												variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
											</Text>
										</div>
										<div className="summary__price-value">
											<Text
												id={priceId}
												tag={'p'}
												variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{formatCurrency(part.price, vatConfig.priceParts[part.type])}
											</Text>
										</div>
									</ConfigurationOverviewPriceWrapperStyle>
								);
							})}
						</div>
					)}
				</div>
			)}
		</>
	);
};

export const SummarySectionCa = ({ financing }: { financing: FinancingData | undefined }) => {
	const { i18n } = useI18n();
	const { loading: isLoadingSelectedEquipment } = useSelectedEquipment();
	const { config } = useMarketConfig();
	const { openDialog } = useDialogs();

	const finProducts = financing?.financingDetails?.detailGroups?.find(({ key }) => key === 'Products');
	const priceDetails = financing?.financingDetails?.detailGroups?.find(({ key }) => key === 'Price Details');
	const displayVehiclePrice = finProducts?.items.find(({ key }) => key === 'VehiclePrice');
	const fees = finProducts?.items.filter(({ key }) =>
		['FederalLuxuryTax', 'FederalAirConditionerTax', 'FreightPDI', 'DealerAdministrationFee'].includes(key),
	);
	const reductions = finProducts?.items.filter(({ key }) => ['DownPayment'].includes(key));
	const totalOptions = finProducts?.items.find(({ key }) => key === 'OptionalEquipment');
	const priceAsBuilt = finProducts?.items.find(({ key }) => key === 'PriceAsBuilt');
	const currentProduct = financing?.financing?.meta?.externalRequestOptions?.Product?.['@ID'];

	return (
		<>
			{isLoadingSelectedEquipment ? (
				<>
					<Skeleton variant={'oval'} height={'324px'} />
					<Skeleton variant={'oval'} height={'324px'} />
					<Skeleton variant={'oval'} height={'324px'} />
				</>
			) : (
				<div className="summary-section">
					{config.showPrices && (
						<>
							<ConfigurationOverviewPriceWrapperStyle>
								{}
								<div className="summary__price-headline" tabIndex={0}>
									<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
										{displayVehiclePrice?.label}
									</Text>
								</div>
								{}
								<div className="summary__price-value" tabIndex={0}>
									<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
										{displayVehiclePrice?.formattedValue}
									</Text>
								</div>
								{config.omnibus.enabled && (
									<div className="summary__omnibus-container" tabIndex={0}>
										<OmnibusDialog />
									</div>
								)}
								{fees && fees?.length > 0 && (
									<div className="title">
										<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
											{i18n('ngc.priceDetails.fees')}
										</Text>
									</div>
								)}
								{fees?.map((item) => (
									<Fragment key={item.key}>
										<div className="summary__price-headline" tabIndex={0}>
											<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{item.label}
											</Text>
										</div>
										<div className="summary__price-value" tabIndex={0}>
											<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
												{item.formattedValue}
											</Text>
										</div>
									</Fragment>
								))}
							</ConfigurationOverviewPriceWrapperStyle>

							<ConfigurationOverviewPriceWrapperStyle tabIndex={0}>
								{totalOptions && (
									<>
										<div className="summary__price-headline" tabIndex={0}>
											<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{totalOptions?.label}
											</Text>
										</div>
										{}
										<div className="summary__price-value" tabIndex={0}>
											<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
												{totalOptions?.formattedValue}
											</Text>
										</div>
									</>
								)}
							</ConfigurationOverviewPriceWrapperStyle>
						</>
					)}
					<FeaturesAndPackages />
					<Accessories />

					<>
						<ConfigurationOverviewPriceWrapperStyle tabIndex={0}>
							{priceAsBuilt && (
								<>
									<div className="summary__price-headline" tabIndex={0}>
										<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
											{priceAsBuilt.label}
										</Text>
									</div>
									{}
									<div className="summary__price-value" tabIndex={0}>
										<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
											{priceAsBuilt.formattedValue}
										</Text>
									</div>
								</>
							)}
							{reductions?.map((item) => (
								<Fragment key={item.key}>
									<div className="summary__price-headline" tabIndex={0}>
										<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
											{item.label}
										</Text>
									</div>
									<div className="summary__price-value" tabIndex={0}>
										<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
											{item.formattedValue}
										</Text>
									</div>
								</Fragment>
							))}
						</ConfigurationOverviewPriceWrapperStyle>
					</>

					{config.showPrices && (
						<>
							<ConfigurationOverviewPriceWrapperStyle key="pricing-information">
								<div className="title">
									<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
										{currentProduct === 'PL'
											? i18n('ngc.priceDetails.lease')
											: currentProduct === 'CC'
												? i18n('ngc.priceDetails.finance')
												: i18n('ngc.priceDetails.cash')}
									</Text>
								</div>
								{priceDetails?.items.map((item) => (
									<Fragment key={item.key}>
										<div className="summary__price-headline" tabIndex={0}>
											<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{item.label}
											</Text>
										</div>
										<div className="summary__price-value" tabIndex={0}>
											<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{item.formattedValue}
											</Text>
										</div>
									</Fragment>
								))}

								<TextLink
									size="sm"
									variant="standalone"
									onClick={() => {
										openDialog('financingDisclaimer');
									}}
									data-testid={DATA_TEST_ID.FINANCING_LAYER_BUTTON}>
									{currentProduct === 'PL'
										? i18n('ngc.priceDetails.showLeaseDetails')
										: currentProduct === 'CC'
											? i18n('ngc.priceDetails.showFinanceDetails')
											: i18n('ngc.priceDetails.showCashDetails')}
								</TextLink>
							</ConfigurationOverviewPriceWrapperStyle>
						</>
					)}
				</div>
			)}
		</>
	);
};
